import { motion } from "framer-motion";
import { Linkedin, Twitter, Mail } from "lucide-react";
import { TEAM, LEADER, CONTACT } from "../data/mock";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

const FlipCard = ({ member, index }) => (
  <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: (index % 3) * 0.1 }}
    data-testid={`team-flip-card-${index}`} className="group h-[420px] [perspective:1200px]">
    <div className="relative w-full h-full transition-transform duration-700 [transform-style:preserve-3d] group-hover:[transform:rotateY(180deg)]">
      {/* Front */}
      <div className="absolute inset-0 rounded-2xl overflow-hidden [backface-visibility:hidden] border border-white/10">
        <img src={member.photo} alt={member.name}
          className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-[filter] duration-500" />
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#1A1014]/90 to-transparent p-6 pt-16">
          <p className="font-heading text-2xl font-semibold text-white">{member.name}</p>
          <p className="text-sm text-[#D4A24C] mt-0.5">{member.role}</p>
        </div>
      </div>
      {/* Back */}
      <div className="absolute inset-0 rounded-2xl bg-[#8C1126] p-8 flex flex-col justify-between [backface-visibility:hidden] [transform:rotateY(180deg)] border border-white/10">
        <div>
          <p className="uppercase text-[10px] tracking-[0.25em] text-[#D4A24C] font-semibold">{member.role}</p>
          <p className="font-heading text-3xl font-semibold text-white mt-2">{member.name}</p>
          <p className="mt-5 text-[15px] text-white/80 leading-relaxed">{member.bio}</p>
        </div>
        <div className="flex gap-3">
          <a href={member.linkedin} target="_blank" rel="noreferrer" data-testid={`team-linkedin-${index}`} aria-label={`${member.name} LinkedIn`}
            className="w-10 h-10 rounded-full border border-white/25 flex items-center justify-center text-white hover:bg-white hover:text-[#8C1126] transition-colors duration-200">
            <Linkedin size={17} />
          </a>
          <a href={member.twitter} target="_blank" rel="noreferrer" data-testid={`team-twitter-${index}`} aria-label={`${member.name} Twitter`}
            className="w-10 h-10 rounded-full border border-white/25 flex items-center justify-center text-white hover:bg-white hover:text-[#8C1126] transition-colors duration-200">
            <Twitter size={17} />
          </a>
          <a href={`mailto:${CONTACT.email}`} data-testid={`team-mail-${index}`} aria-label={`Email ${member.name}`}
            className="w-10 h-10 rounded-full border border-white/25 flex items-center justify-center text-white hover:bg-white hover:text-[#8C1126] transition-colors duration-200">
            <Mail size={17} />
          </a>
        </div>
      </div>
    </div>
  </motion.div>
);

export default function Team() {
  return (
    <div data-testid="team-page" className="pt-16 bg-[#2A0208]">
      <section className="py-24 sm:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <motion.div {...fadeUp} className="max-w-3xl mb-16">
            <p className="uppercase text-xs tracking-[0.2em] text-[#D4A24C] font-semibold mb-4">The Team</p>
            <h1 className="font-heading text-5xl sm:text-6xl font-light tracking-tight leading-[1.05] text-white">
              The minds behind <span className="italic text-[#D4A24C]">your growth.</span>
            </h1>
            <p className="mt-6 text-lg text-white/65 leading-relaxed">
              Strategists, designers and builders united by one obsession — making your business impossible to ignore. Hover a card to meet them.
            </p>
          </motion.div>

          {/* Featured leader */}
          <motion.div {...fadeUp} whileHover={{ y: -6 }} data-testid="team-leader-card"
            className="group mb-14 rounded-2xl overflow-hidden border border-[#D4A24C]/25 bg-gradient-to-br from-[#3D1224] to-[#2A0208] grid grid-cols-1 md:grid-cols-[280px_1fr] transition-[border-color,box-shadow] duration-500 hover:border-[#D4A24C]/70 hover:shadow-[0_35px_80px_-25px_rgba(212,162,76,0.45)]">
            <div className="relative bg-[#4A1830] min-h-[280px] flex items-center justify-center overflow-hidden">
              {LEADER.photo ? (
                <img src={LEADER.photo} alt={LEADER.name} className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-110 transition-all duration-700 ease-out" />
              ) : (
                <div className="flex flex-col items-center justify-center text-center px-6 py-12">
                  <div className="w-24 h-24 rounded-full bg-[#D4A24C]/15 border-2 border-[#D4A24C]/40 flex items-center justify-center">
                    <span className="font-heading text-4xl font-semibold text-[#D4A24C]">MH</span>
                  </div>
                  <p className="mt-4 text-xs uppercase tracking-[0.2em] text-[#D4A24C]/70">Photo coming soon</p>
                </div>
              )}
            </div>
            <div className="p-8 sm:p-10 flex flex-col justify-center">
              <span className="inline-flex w-fit items-center rounded-full bg-[#D4A24C] text-[#2A0208] text-[11px] font-bold uppercase tracking-[0.15em] px-3 py-1">{LEADER.role}</span>
              <h2 className="mt-4 font-heading text-4xl sm:text-5xl font-light text-white">{LEADER.name}</h2>
              <p className="mt-4 text-[15px] sm:text-base text-white/75 leading-relaxed max-w-2xl">{LEADER.bio}</p>
              <div className="flex gap-3 mt-6">
                <a href={LEADER.linkedin} target="_blank" rel="noreferrer" data-testid="team-leader-linkedin" aria-label={`${LEADER.name} LinkedIn`}
                  className="w-10 h-10 rounded-full border border-white/25 flex items-center justify-center text-white hover:bg-[#D4A24C] hover:text-[#2A0208] hover:border-[#D4A24C] transition-colors duration-200">
                  <Linkedin size={17} />
                </a>
                <a href={LEADER.twitter} target="_blank" rel="noreferrer" data-testid="team-leader-twitter" aria-label={`${LEADER.name} Twitter`}
                  className="w-10 h-10 rounded-full border border-white/25 flex items-center justify-center text-white hover:bg-[#D4A24C] hover:text-[#2A0208] hover:border-[#D4A24C] transition-colors duration-200">
                  <Twitter size={17} />
                </a>
                <a href={`mailto:${CONTACT.email}`} data-testid="team-leader-mail" aria-label={`Email ${LEADER.name}`}
                  className="w-10 h-10 rounded-full border border-white/25 flex items-center justify-center text-white hover:bg-[#D4A24C] hover:text-[#2A0208] hover:border-[#D4A24C] transition-colors duration-200">
                  <Mail size={17} />
                </a>
              </div>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {TEAM.map((m, i) => <FlipCard key={m.name} member={m} index={i} />)}
          </div>
        </div>
      </section>
    </div>
  );
}
